import { HttpInterceptorFn } from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { catchError, tap, throwError } from 'rxjs'
import { environment } from '../../environments/environment'
import { AuthService } from './auth.service'
import { DOC_PLAYGROUND_REQUEST } from './http-context-tokens'

// In-memory CSRF token — captured from the X-CSRF-Token response header on every API
// response. This works cross-origin where document.cookie cannot read the API domain's
// _csrf cookie. The cookie-based fallback handles same-origin dev environments.
let csrfTokenMemory = ''

function getCsrfToken(): string {
  if (csrfTokenMemory) return csrfTokenMemory
  // Fallback: same-origin environments where JS can read the cookie directly.
  const value = `; ${document.cookie}`
  const parts = value.split(`; _csrf=`)
  if (parts.length === 2) return parts.pop()!.split(';').shift() ?? ''
  return ''
}

const CSRF_MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService)
  const router = inject(Router)

  const isApiReq = req.url.startsWith(environment.apiBaseUrl)
  if (!isApiReq) {
    return next(req).pipe(
      catchError(err => {
        if (err.status === 403 && err.error?.errors?.[0]?.detail === 'Actions are disabled during impersonation') {
          authService.showReadOnlyAlert()
        }
        return throwError(() => err)
      }),
    )
  }

  // Send cookies (including HttpOnly auth cookies) on all API requests.
  let outgoing = req.clone({ withCredentials: true })

  // In KC cookie mode session.id is empty — strip the invalid `Authorization: Bearer `
  // header so the backend reads auth from the HttpOnly kc_access_token cookie instead.
  const bearerValue = outgoing.headers.get('Authorization') ?? ''
  if (bearerValue === 'Bearer ' || bearerValue === 'Bearer') {
    outgoing = outgoing.clone({ headers: outgoing.headers.delete('Authorization') })
  }

  // Attach the CSRF double-submit token for state-mutating requests.
  // The backend requires X-CSRF-Token when no valid Bearer token is present.
  if (CSRF_MUTATING_METHODS.has(req.method)) {
    const csrf = getCsrfToken()
    if (csrf) {
      outgoing = outgoing.clone({ headers: outgoing.headers.set('X-CSRF-Token', csrf) })
    }
  }

  // The docs "Try it" console sends whatever the user typed as an org API key —
  // it is never the caller's own session, so a 401 from it must not log the real
  // session out. Detected via an explicit request tag (org-api-keys.service.ts),
  // not by inspecting the header value: a user testing an invalid key won't
  // necessarily type something shaped like a real uwk_-prefixed key.
  const isDocPlaygroundRequest = req.context.get(DOC_PLAYGROUND_REQUEST)

  return next(outgoing).pipe(
    tap(event => {
      // Capture the CSRF token echoed in every response header so mutations that
      // follow immediately (including on first load) always have a valid token.
      if ('headers' in event) {
        const token = event.headers.get('X-CSRF-Token')
        if (token) csrfTokenMemory = token
      }
    }),
    catchError(err => {
      if (err.status === 401 && !isDocPlaygroundRequest) {
        authService.clearSession()
        authService.clearImpersonation()
        router.navigate(['/trading-hub'])
      }
      if (err.status === 403 && err.error?.errors?.[0]?.detail === 'Actions are disabled during impersonation') {
        authService.showReadOnlyAlert()
      }
      return throwError(() => err)
    }),
  )
}
